import {FC, useState} from 'react'
import {Calendar, CalendarProps} from '@components/commons/calendar'
import {InputText, InputTextProps} from '@components/commons/input-text'
import {Dialog, DialogContent, DialogTrigger} from '@components/ui/dialog'
import {Popover, PopoverContent, PopoverTrigger} from '@components/ui/popover'

type DatePickerProps = {
    asModal?: boolean
    formatter: (date: Date) => string
    onBlur?: () => void
    disabled?: boolean
    errorMessage?: string
    mode: 'single' | 'range' | 'multiple'
    triggerProps?: Omit<InputTextProps, 'type' | 'readonly' | 'value'>
} & Omit<CalendarProps, 'mode'>

type FormatDateFnArgs = {
    selectedDates: Date[]
    formatter: (date: Date) => string
}
const formatFnByMode = {
    single: ({selectedDates, formatter}: FormatDateFnArgs) => {
        const date = selectedDates[0]
        return date ? formatter(date) : ''
    },
    multiple: ({selectedDates, formatter}: FormatDateFnArgs) => {
        return selectedDates.map(date => formatter(date))
    },
    range: ({selectedDates, formatter}: FormatDateFnArgs) => {
        const [start, end] = selectedDates
        if (!start && !end) {
            return ''
        }
        const formattedStart = start ? formatter(start) : ''
        const formattedEnd = end ? formatter(end) : ''

        return `${formattedStart} - ${formattedEnd}`
    }
} as const

export const DatePicker: FC<DatePickerProps> = ({
    asModal,
    mode,
    selectedDates,
    triggerProps,
    formatter,
    onBlur,
    disabled,
    errorMessage,
    onDatesChange,
    ...props
}) => {
    const [open, setOpen] = useState(false)

    const trigger = (
        <InputText
            disabled={disabled}
            {...triggerProps}
            type="text"
            value={formatFnByMode[mode]({selectedDates, formatter})}
            readOnly
            errorMessage={errorMessage}
        />
    )

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen) {
            onBlur?.()
        }
    }

    const handleDatesChange = (dates: Date[]) => {
        onDatesChange(dates)
        // In single mode a pick completes the selection, so close the overlay.
        if (mode === 'single' && dates.length > 0) {
            handleOpenChange(false)
        }
    }

    const calendar = <Calendar {...props} mode={mode} selectedDates={selectedDates} onDatesChange={handleDatesChange} />

    if (asModal) {
        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogTrigger disabled={disabled} nativeButton={false} render={trigger} />
                <DialogContent className="rounded-sm" showCloseButton={false} forceRenderOverlay={true}>
                    {calendar}
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger disabled={disabled} nativeButton={false} render={trigger} />
            <PopoverContent className="rounded-sm border-0">{calendar}</PopoverContent>
        </Popover>
    )
}
